import { UseSearchResult as UseBossSearchResult } from "@saleor/hooks/makeBossSearch";
import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import { removeBase64Name } from "@saleor/reports/utils/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchPayoutMalls,
  SearchPayoutMallsVariables
} from "@saleor/searches/types/SearchPayoutMalls";
import { SettlementStatusEnum } from "@saleor/types/globalTypes";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../../utils/filters";
import {
  FinanceOrderBoolean,
  FinanceOrderFilterKeys,
  FinanceOrderListFilterOpts,
  PaymentType
} from "../../components/FinanceOrderListPage/filters";
import { InitialFinanceOrderFilterData_brands_edges_node } from "../../types/InitialFinanceOrderFilterData";
import { PayoutMalls_payoutMalls_edges_node } from "../../types/PayoutMalls";
import {
  FinanceOrderListUrlFilters,
  FinanceOrderListUrlFiltersEnum,
  FinanceOrderListUrlFiltersWithMultipleValuesEnum,
  FinanceOrderListUrlQueryParams
} from "../../urls";
import { getMoment } from "../../utils/handlers";

export const FINANCE_ORDERS_FILTERS_KEY = "financeOrdersFilters";

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<FinanceOrderListUrlFilters>(
  FINANCE_ORDERS_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  FinanceOrderListUrlQueryParams,
  FinanceOrderListUrlFilters
>({
  ...FinanceOrderListUrlFiltersEnum,
  ...FinanceOrderListUrlFiltersWithMultipleValuesEnum
});

export function getFilterQueryParam(
  filter: IFilterElement<FinanceOrderFilterKeys>
): FinanceOrderListUrlFilters {
  const { name } = filter;
  switch (name) {
    case FinanceOrderFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        FinanceOrderListUrlFiltersWithMultipleValuesEnum.brand
      );
    case FinanceOrderFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        FinanceOrderListUrlFiltersEnum.createdFrom,
        FinanceOrderListUrlFiltersEnum.createdTo
      );
    case FinanceOrderFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        FinanceOrderListUrlFiltersWithMultipleValuesEnum.mall
      );
    case FinanceOrderFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        FinanceOrderListUrlFiltersEnum.orderCompleteDateFrom,
        FinanceOrderListUrlFiltersEnum.orderCompleteDateTo
      );
    case FinanceOrderFilterKeys.isPayoutEligible:
      return getSingleEnumValueQueryParam(
        filter,
        FinanceOrderListUrlFiltersEnum.isPayoutEligible,
        FinanceOrderBoolean
      );
    case FinanceOrderFilterKeys.paymentMethodType:
      return getSingleEnumValueQueryParam(
        filter,
        FinanceOrderListUrlFiltersEnum.paymentMethodType,
        PaymentType
      );
  }
}

export function getFilterOpts(
  brands: {
    initial: InitialFinanceOrderFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: PayoutMalls_payoutMalls_edges_node[];
    search: UseBossSearchResult<SearchPayoutMalls, SearchPayoutMallsVariables>;
  },
  params: FinanceOrderListUrlFilters
): FinanceOrderListFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    isPayoutEligible: {
      active: !!params.isPayoutEligible,
      value: maybe(() => params.isPayoutEligible, "")
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderDate: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    paymentMethodType: {
      active: !!params.paymentMethodType,
      value: maybe(() => params.paymentMethodType, "")
    }
  };
}

export function getFilterVariables(params: FinanceOrderListUrlFilters) {
  // Today + 1 day to cover time difference
  const today = getMoment("YYYY-MM-DD", 1);
  const yesterday = getMoment("YYYY-MM-DD", -1);

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    isPayoutEligible: params?.isPayoutEligible
      ? params.isPayoutEligible === "true"
      : null,
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    orderDate: getGteLteVariables({
      gte: params.createdFrom
        ? moment(`${params.createdFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.createdFrom && params.orderCompleteDateFrom
        ? null
        : yesterday,
      lte: params.createdTo
        ? moment(`${params.createdTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.createdTo && params.orderCompleteDateTo
        ? null
        : today
    }),
    // orderStatus: ["completed"],
    paymentMethodType: params.paymentMethodType,
    search: params.query ? params.query : "",
    settlementStatus:
      params.payoutStatus === SettlementStatusEnum.OPEN
        ? [SettlementStatusEnum.OPEN, SettlementStatusEnum.REOPENED]
        : [params.payoutStatus],
    storeOrderStatus: ["completed"]
  };
}
